// Import-run journal in IndexedDB (survives localStorage.clear() — the sidebar
// Logout button wipes localStorage). The journal is a hint, never truth: the
// runner always reconciles against the server before creating anything.
//
// `scope` separates journals of different run kinds on the same tour: the
// create-mode wizard uses the default scope ("tour189"), the add-languages mode
// uses "addlang" ("tour189.addlang") so an unfinished add-languages run can never
// make the create-mode preflight believe it may resume a stop-creating import.

const DB_NAME = "ttStopsImport";
const STORE = "journal";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export interface JournalEntry {
  ts: string;
  type: "upload" | "stop_created" | "stop_updated" | "tail_done" | "verify";
  sl?: number;
  kind?: "image" | "audio";
  lang?: string;
  file?: string;
  id?: number | null;
  [k: string]: any;
}

const key = (tourId: number, scope = "") => `tour${tourId}${scope ? `.${scope}` : ""}`;

export const journal = {
  async readAll(tourId: number, scope = ""): Promise<JournalEntry[]> {
    return (await withStore<JournalEntry[]>("readonly", (s) => s.get(key(tourId, scope)))) || [];
  },

  async append(tourId: number, entry: Omit<JournalEntry, "ts">, scope = ""): Promise<void> {
    const all = await this.readAll(tourId, scope);
    all.push({ ts: new Date().toISOString(), ...entry } as JournalEntry);
    await withStore("readwrite", (s) => s.put(all, key(tourId, scope)));
  },

  // uploaded server-filenames per stop, and which stops were created/updated
  async summarize(
    tourId: number,
    scope = ""
  ): Promise<{ uploads: Record<number, { image: string | null; audio: Record<string, string> }>; created: Set<number> }> {
    const uploads: Record<number, { image: string | null; audio: Record<string, string> }> = {};
    const created = new Set<number>();
    for (const e of await this.readAll(tourId, scope)) {
      if (e.type === "upload" && e.sl != null) {
        uploads[e.sl] = uploads[e.sl] || { image: null, audio: {} };
        if (e.kind === "image") uploads[e.sl].image = e.file || null;
        else if (e.lang) uploads[e.sl].audio[e.lang] = e.file || "";
      } else if ((e.type === "stop_created" || e.type === "stop_updated") && e.sl != null) {
        created.add(e.sl);
      }
    }
    return { uploads, created };
  },

  // Archive after a successful run so a future fresh import into the same tour
  // can never replay stale filenames.
  async archive(tourId: number, scope = ""): Promise<void> {
    const all = await this.readAll(tourId, scope);
    if (all.length) await withStore("readwrite", (s) => s.put(all, `${key(tourId, scope)}.done-${Date.now()}`));
    await withStore("readwrite", (s) => s.delete(key(tourId, scope)));
  },

  async hasActive(tourId: number, scope = ""): Promise<boolean> {
    return (await this.readAll(tourId, scope)).length > 0;
  },
};
